/*
 * Copyright Evollis 2022
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { DELIVERY_METHOD_CODES } from "../../../constants/DeliveryMethods"
import { TRANSACTION_STATUS } from "../../../constants/TransactionStatus"
import { loc } from "../../../core/utils"
import { TunnelDataContext } from "../../../digital-journey/context/TunnelDataContext"
import usePatchOrder from "../../../hooks/usePatchOrder"
import EditOrderForm from "./EditOrderForm"
import { format, parse } from "date-fns"
import moment from "moment"
import React, { useContext, useState } from "react"
import { Button, Card, Col, Modal, Row, Spinner } from "react-bootstrap"
import "react-day-picker/dist/style.css"
import * as FinalForm from "react-final-form"

export default function Order({
  firstname,
  lastname,
  product,
  opl,
  date,
  shippingDate,
  deliveryMethod,
  status,
  redirectionUrl,
  orderLanguageTag
}) {
  const [show, setShow] = useState(false)

  const { tunnelData } = useContext(TunnelDataContext)
  const { mutate: patchOrder, isMutating: isPatchingOrder } = usePatchOrder()

  const initialValues = {
    shippingDate: format(new Date(), "y-MM-dd")
  }

  const onSubmit = (values) => {
    const { shippingDate } = values
    const dto = {
      shippingDate
    }
    patchOrder(
      { tunnelData, opl, values: dto },
      {
        onSuccess: () => {
          setShow(false)
        }
      }
    )
  }

  function getOrderLocale(languageTag) {
    if (!languageTag) return "fr"
    const locale = languageTag.split("-")[0]
    return locale ?? "fr"
  }

  function isAddShippingDateDisplayed(status, deliveryMethod) {
    return (
      [TRANSACTION_STATUS.ACCEPTED_AWAITING_FUNDING].includes(status) &&
      [DELIVERY_METHOD_CODES.ON_ORDER, DELIVERY_METHOD_CODES.DELAYED_TAKEWAY].includes(deliveryMethod)
    )
  }

  return (
    <Card className="mb-2 cursor-pointer">
      <Card.Body onClick={() => window.open(redirectionUrl, "_self")}>
        <Row style={{ justifyContent: "space-around" }}>
          <Col sm={6}>
            <span className="font-weight-bold" style={{ color: "black" }}>
              {firstname + " " + lastname}
            </span>
            <p>{product}</p>
          </Col>
          <Col sm={5} className="text-end">
            <span className="font-weight-bold" style={{ color: "black" }}>
              {opl}
            </span>
            <p>{date}</p>
            <p>{loc(status)}</p>
          </Col>
        </Row>
        <Row className="justify-content-end">
          {!shippingDate && isAddShippingDateDisplayed(status, deliveryMethod) && (
            <Col md={12} lg={5} xl={4}>
              <Button
                role="button"
                className="add-shipping-date-button"
                aria-label={loc("resume_order_fill_shipping_date_button_label")}
                type="button"
                size="sm"
                variant="outline-secondary"
                onClick={(event) => {
                  event.stopPropagation()
                  setShow(true)
                }}
              >
                <i className="fal fa-pencil" />
                <span>{loc("resume_order_fill_shipping_date_button_label")}</span>
              </Button>
            </Col>
          )}
        </Row>
      </Card.Body>

      <Modal show={show} onHide={() => setShow(false)} backdrop="static" keyboard={false}>
        <Modal.Header closeButton={true}>{loc("resume_order_fill_shipping_date_button_label")}</Modal.Header>
        <Modal.Body>
          <FinalForm.Form
            initialValues={initialValues}
            onSubmit={onSubmit}
            render={({ handleSubmit, values }) => (
              <>
                {isPatchingOrder ? (
                  <div className="sprinner-container">
                    <Spinner className="spinner" animation="border" />
                  </div>
                ) : (
                  <EditOrderForm
                    orderLocale={getOrderLocale(orderLanguageTag)}
                    dateFromOrder={moment(date, "DD/MM/YYYY HH:mm").toDate()}
                    handleSubmit={handleSubmit}
                    selected={parse(values?.shippingDate, "y-MM-dd", new Date())}
                  />
                )}
              </>
            )}
          ></FinalForm.Form>
        </Modal.Body>
      </Modal>
    </Card>
  )
}
